import { catchError, map, tap, throwError } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { ShippingStatusEnum } from '../enums/shipping-status.enum';
import { IShippingStop } from '../interfaces/shipping-stop.interface';
import { IShippingWebhookRequest } from '../interfaces/shipping-webhook.interface';
import { IShipping } from '../interfaces/shipping.interface';
import { AnalyticsService } from './analytics.service';

@Injectable({ providedIn: 'root' })
export class ShippingService {
  private http = inject(HttpClient);
  private analytics = inject(AnalyticsService);

  private isFinalized(stop: IShippingStop, status: ShippingStatusEnum): boolean {
    if (status === ShippingStatusEnum.SHIPPING_ROUTE && stop.kind.toString().toLowerCase().includes('pickup')) {
      return true;
    }
    return stop.finalized;
  }

  fetchShipping(shippingReference: string) {
    return this.http.get<IShipping>(`/api/self-shippings/v1/orders/${shippingReference}`).pipe(
      map((res) => ({
        ...res,
        stops: res.stops.map((stop, i) => {
          const stopId = i + 1;
          const finalized = this.isFinalized(stop, res.status);
          return { ...stop, id: stopId, finalized };
        }),
      })),
      tap((res) => {
        this.analytics.callback('fetch_shipping', { response: res, reference_hash: shippingReference, success: true });
      }),
      catchError((error) => {
        this.analytics.callback('fetch_shipping', { response: error, reference_hash: shippingReference, sucess: false });
        return throwError(() => error);
      })
    );
  }

  webhook(payload: IShippingWebhookRequest, shippingReference: string) {
    return this.http.post(`/api/self-shippings/v1/status`, payload).pipe(
      tap((response) => {
        this.analytics.callback('update_shipping_status', { payload, response: response, reference_hash: shippingReference, sucess: true });
      }),
      catchError((error) => {
        this.analytics.callback('update_shipping_status', { payload, response: error, reference_hash: shippingReference, sucess: false });
        return throwError(() => error);
      })
    );
  }

  finalizeStop(shippingReference: string, stop: IShippingStop, proof?: { uploadId: string; receiverName?: string }) {
    return this.http.patch<IShipping>(`/api/self-shippings/v1/orders/${shippingReference}/stops`, {
      index: stop.index,
      finalized: stop.finalized,
      uploadId: proof?.uploadId,
      receiverName: proof?.receiverName,
    }).pipe(
      tap((response) => {
        this.analytics.callback('finalize_stop', { payload: stop, response: response, reference_hash: shippingReference, sucess: true });
      }),
      catchError((error) => {
        this.analytics.callback('finalize_stop', { payload: stop, response: error, reference_hash: shippingReference, sucess: false });
        return throwError(() => error);
      })
    );
  }
}
